import { Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { FinanceInventoryLink } from "../FinanceInventoryLink";
import { InternalTypographyLink } from "../InternalLink";
import { CustomTable } from "../Tables/CustomTable";
import { ExpiringLeaseItem } from "~/types/finance";
import { EXPIRING_LEASES_MOBILE_CONFIG } from "./constants";
import { formatExpirationLabel, getTenantDisplayName } from "./utils";

const buildColumns = (
  t: TFunction<"translation">,
): ColumnsType<ExpiringLeaseItem> => [
  {
    title: t("Tenant"),
    key: "tenant",
    width: 180,
    render: (_, record) => {
      const name = getTenantDisplayName(record);
      if (record.tenantResolved === false || !record.tenant?.id) {
        return name;
      }
      return (
        <InternalTypographyLink to={`/tenant/${record.tenant.id}`}>
          {name}
        </InternalTypographyLink>
      );
    },
  },
  {
    title: `${t("Unit")} / ${t("Parcel")}`,
    key: "unit",
    width: 180,
    render: (_, record) => (
      <FinanceInventoryLink
        entityType={record.inventoryEntityType}
        entityID={record.unit?.id}
        name={record.unit?.name}
      />
    ),
  },
  {
    title: t("Property"),
    key: "property",
    width: 180,
    render: (_, record) =>
      record.property?.id ? (
        <InternalTypographyLink to={`/property/${record.property.id}`}>
          {record.property.name || record.property.id}
        </InternalTypographyLink>
      ) : (
        "—"
      ),
  },
  {
    title: t("Lease_End_Date"),
    key: "endDate",
    width: 220,
    sorter: (a, b) => a.daysUntilEnd - b.daysUntilEnd,
    defaultSortOrder: "ascend",
    render: (_, record) => formatExpirationLabel(record, t),
  },
  {
    title: t("Actions"),
    key: "viewLease",
    width: 120,
    render: (_, record) => (
      <InternalTypographyLink to={`/lease/${record.leaseID}`}>
        {t("View")}
      </InternalTypographyLink>
    ),
  },
];

export type ExpiringLeasesTableProps = {
  items: ExpiringLeaseItem[];
  rowKeyPrefix: string;
  emptyText: string;
};

export const ExpiringLeasesTable = ({
  items,
  rowKeyPrefix,
  emptyText,
}: ExpiringLeasesTableProps) => {
  const { t } = useTranslation();

  if (items.length === 0) {
    return <Typography.Paragraph type="secondary">{emptyText}</Typography.Paragraph>;
  }

  const columns = buildColumns(t);

  return (
    <CustomTable
      style={{ marginTop: 0 }}
      tableHeaders={null}
      mobileCardConfig={EXPIRING_LEASES_MOBILE_CONFIG}
      mobileDefaultSort={{
        dataIndex: "daysUntilEnd",
        order: "ascend",
      }}
      tableProps={{
        bordered: true,
        scroll: { x: 900 },
        dataSource: items,
        columns,
        rowKey: (record) => `${rowKeyPrefix}-${record.leaseID}`,
        pagination: items.length > 10 ? { pageSize: 10 } : false,
      }}
    />
  );
};
